import { integrationModuleUrl, integrationMarkup } from "./design-mode";
import React, { useEffect, useState } from "react";
import { createRoot, type Root } from "react-dom/client";
import { z } from "zod";
import {
  FormPresentation,
  formDimension,
  formPresentationCss,
} from "./form-presentation";
import {
  authoringModes,
  integrationOptionsSchema,
  type AuthoringMode,
} from "./integration-options";

const dimension = z
  .string()
  .max(80)
  .refine(
    (value) => !!formDimension(value, ""),
    "Enter pixels, %, rem, em, viewport units or auto",
  );
export const integrationModalOptionsSchema = z
  .object({
    title: z.string().trim().min(1).max(200).default("Customize document"),
    triggerLabel: z.string().trim().min(1).max(200).default("Open integration"),
    width: dimension.default("1120px"),
    height: dimension.default("80vh"),
    maxWidth: dimension.default("96vw"),
    maxHeight: dimension.default("90vh"),
    configurable: z.boolean().default(true),
    designMode: z.boolean().default(false),
  })
  .strict();
export type IntegrationModalOptions = z.input<
  typeof integrationModalOptionsSchema
>;
const attributes = {
  title: "dialog-title",
  triggerLabel: "trigger-label",
  width: "width",
  height: "height",
  maxWidth: "max-width",
  maxHeight: "max-height",
} as const;
const modeNames: Record<AuthoringMode, string> = {
  configuration: "Fields & variables",
  "reusable-forms": "Reusable form builder",
  designer: "Document designer",
  outputs: "Output profiles",
  "global-profiles": "Global profiles",
  "validation-presets": "Validation presets",
  "formatting-presets": "Formatting presets",
  summary: "Project summary",
};
type Child = HTMLElement & { options?: unknown };
const ElementBase =
  typeof HTMLElement === "undefined"
    ? (class {} as typeof HTMLElement)
    : HTMLElement;
type ModalChange = Partial<IntegrationModalOptions> & {
  mode?: string;
  showProof?: boolean;
};
function ModalConfiguration({
  settings,
  modes,
  mode,
  form,
  showProof,
  canProof,
  onChange,
  child,
  designMode,
}: {
  settings: z.output<typeof integrationModalOptionsSchema>;
  modes: AuthoringMode[];
  mode: string;
  form: boolean;
  showProof: boolean;
  canProof: boolean;
  onChange: (patch: ModalChange) => void;
  child?: Child;
  designMode: boolean;
}) {
  const [draft, setDraft] = useState(settings),
    [error, setError] = useState(""),
    [source, setSource] = useState<string>();
  const signature = JSON.stringify(settings);
  useEffect(() => {
    setDraft(settings);
    setError("");
  }, [signature]);
  return (
    <section aria-label="Popout configuration" className="popout-settings">
      <h3>Popout configuration</h3>
      <p>
        Configure this window without replacing the mounted integration. Applied
        project edits and form values stay available after closing and
        reopening.
      </p>
      <div className="popout-grid">
        {modes.length > 0 && (
          <label>
            Integration view
            <select
              aria-label="Integration view"
              value={mode}
              onChange={(e) => onChange({ mode: e.target.value })}
            >
              {modes.map((value) => (
                <option key={value} value={value}>
                  {modeNames[value]}
                </option>
              ))}
            </select>
          </label>
        )}
        {(
          [
            ["title", "Popout title"],
            ["triggerLabel", "Popout launch label"],
            ["width", "Popout width"],
            ["height", "Popout height"],
            ["maxWidth", "Popout maximum width"],
            ["maxHeight", "Popout maximum height"],
          ] as const
        ).map(([key, label]) => (
          <label key={key}>
            {label}
            <input
              aria-label={label}
              value={draft[key]}
              onChange={(e) =>
                setDraft((value) => ({ ...value, [key]: e.target.value }))
              }
            />
          </label>
        ))}
      </div>
      {form && (
        <label>
          <input
            type="checkbox"
            checked={showProof}
            disabled={!canProof}
            onChange={(e) => onChange({ showProof: e.target.checked })}
          />{" "}
          Show document proof
        </label>
      )}
      <p>Sizes accept pixels, %, rem, em, viewport units or auto.</p>
      <button
        type="button"
        onClick={() => {
          const result = integrationModalOptionsSchema.safeParse(draft);
          if (!result.success) {
            setError(result.error.issues[0].message);
            return;
          }
          setError("");
          onChange(result.data);
        }}
      >
        Apply popout settings
      </button>
      {designMode && (
        <section aria-label="Integration design mode">
          <h3>Design mode</h3>
          {(child?.localName === "proof-authoring" ||
            child?.localName === "proof-form") && (
            <>
              {(["edit", "proof", "export"] as const).map((key) => (
                <label key={key}>
                  <input
                    type="checkbox"
                    checked={
                      (child.options as any)?.permissions?.[key] !== false
                    }
                    onChange={(event) => {
                      const options = (child.options || {}) as any;
                      child.options = {
                        ...options,
                        permissions: {
                          ...options.permissions,
                          [key]: event.target.checked,
                        },
                      };
                      onChange({});
                    }}
                  />
                  Allow {key}
                </label>
              ))}
              {(["primary", "accent"] as const).map((key) => (
                <label key={key}>
                  {key} color
                  <input
                    type="color"
                    value={(child.options as any)?.theme?.[key] || "#48745f"}
                    onChange={(event) => {
                      const options = (child.options || {}) as any;
                      child.options = {
                        ...options,
                        theme: { ...options.theme, [key]: event.target.value },
                      };
                      onChange({});
                    }}
                  />
                </label>
              ))}
            </>
          )}

          <p>
            Changes apply to the mounted integration. Host data and credentials
            are not included in generated source.
          </p>
          {(
            [
              ["allowProjectRename", "Allow project name editing"],
              ["enableStatements", "Enable statements"],
              ["enableRules", "Enable calculated rules and presets"],
              ["enableFormatting", "Enable formatting"],
              ["enableValidation", "Enable validation"],
              ["enableDynamicSources", "Enable dynamic data sources"],
              ["enableConditionalVisibility", "Enable conditional visibility"],
              ["enablePartOverrides", "Enable part overrides"],

              ["showHeading", "Show editor heading"],
              ["showDescription", "Show editor description"],
            ] as const
          ).map(([key, label]) => (
            <label key={key}>
              <input
                type="checkbox"
                checked={
                  !!(
                    (child?.options as any)?.editor?.[key] ??
                    (child?.options as any)?.services?.editorPresentation?.[
                      key
                    ] ??
                    key.startsWith("enable")
                  )
                }
                onChange={(event) => {
                  if (!child) return;
                  const current = (child.options || {}) as any;
                  child.options =
                    child.localName === "proof-authoring"
                      ? {
                          ...current,
                          editor: {
                            ...current.editor,
                            [key]: event.target.checked,
                          },
                        }
                      : {
                          ...current,
                          services: {
                            ...current.services,
                            editorPresentation: {
                              ...current.services?.editorPresentation,
                              [key]: event.target.checked,
                            },
                          },
                        };
                  onChange({});
                }}
              />
              {label}
            </label>
          ))}
          <button
            type="button"
            onClick={() => {
              if (child) {
                const options = child.options;
                child.options = options;
              }
              onChange({});
            }}
          >
            Reload preview
          </button>
          <button
            type="button"
            onClick={() => {
              const options = (child?.options || {}) as any;
              const portable = {
                ...(options.theme ? { theme: options.theme } : {}),
                ...(options.permissions
                  ? { permissions: options.permissions }
                  : {}),
                ...(options.allowedModes
                  ? { allowedModes: options.allowedModes }
                  : {}),
                ...(options.editor ? { editor: options.editor } : {}),
                ...(options.services?.editorPresentation
                  ? {
                      services: {
                        editorPresentation: options.services.editorPresentation,
                      },
                    }
                  : {}),
              };
              const tag = child?.localName || "proof-form";
              const moduleName = tag
                .replace(/^proof-/, "")
                .replace("reusable-form", "reusable-forms");
              setSource(
                `<proof-integration-modal id="proof-popup">${integrationMarkup(child || undefined, "proof-integration")}</proof-integration-modal>\n<script type="module">\nimport ${JSON.stringify(integrationModuleUrl(child || undefined))};\nimport ${JSON.stringify(integrationModuleUrl({ localName: "proof-integration-modal" }))};\ndocument.getElementById("proof-popup").configuration = ${JSON.stringify(settings, null, 2).replaceAll("<", "\\u003c")};\nconst integration = document.getElementById("proof-integration");\nintegration.options = ${JSON.stringify(portable, null, 2).replaceAll("<", "\\u003c")};\n// Supply your authenticated host data and service callbacks separately.\n</script>`,
              );
            }}
          >
            Generate source
          </button>
          {source !== undefined && (
            <dialog
              ref={(element) => {
                if (element && !element.open) element.showModal();
              }}
              onCancel={() => setSource(undefined)}
              style={{
                width: "min(800px,90vw)",
                maxHeight: "85dvh",
                margin: "auto",
                padding: 24,
              }}
            >
              <h3>Integration implementation source</h3>
              <textarea
                aria-label="Generated integration source"
                readOnly
                value={source}
                rows={16}
                style={{ width: "100%", boxSizing: "border-box" }}
              />
              <button type="button" onClick={() => setSource(undefined)}>
                Close source
              </button>
            </dialog>
          )}
        </section>
      )}
      {error && <p role="alert">{error}</p>}
    </section>
  );
}

/** Wrap existing integrations without owning their authentication, project or values. */
export class ProofIntegrationModalElement extends ElementBase {
  static observedAttributes = [
    ...Object.values(attributes),
    "configurable",
    "design-mode",
    "open",
  ];
  private root?: Root;
  private observer?: MutationObserver;
  private previousFocus?: HTMLElement;
  get configuration(): z.output<typeof integrationModalOptionsSchema> {
    const defaults = integrationModalOptionsSchema.parse({});
    return integrationModalOptionsSchema.parse({
      ...Object.fromEntries(
        Object.entries(attributes).map(([key, attribute]) => [
          key,
          key === "title" || key === "triggerLabel"
            ? this.getAttribute(attribute)?.trim().slice(0, 200) ||
              defaults[key as keyof typeof attributes]
            : formDimension(
                this.getAttribute(attribute),
                defaults[key as keyof typeof attributes],
              ),
        ]),
      ),
      configurable: this.hasAttribute("configurable"),
      designMode: this.hasAttribute("design-mode"),
    });
  }
  set configuration(value: IntegrationModalOptions) {
    const next = integrationModalOptionsSchema.parse(value);
    for (const [key, attribute] of Object.entries(attributes))
      this.setAttribute(attribute, next[key as keyof typeof attributes]);
    this.toggleAttribute("configurable", next.configurable);
    this.toggleAttribute("design-mode", next.designMode);
  }
  showModal = () => {
    this.previousFocus = this.ownerDocument.activeElement as HTMLElement;
    this.setAttribute("open", "");
  };
  close = () => {
    this.removeAttribute("open");
  };
  connectedCallback() {
    if (!this.shadowRoot) {
      const shadow = this.attachShadow({ mode: "open" });
      const style = document.createElement("style");
      style.textContent = formPresentationCss + modalCss;
      const mount = document.createElement("div");
      shadow.append(style, mount);
    }
    this.root ||= createRoot(this.shadowRoot!.querySelector("div")!);
    this.observer?.disconnect();
    this.observer = new MutationObserver(() => this.render());
    this.observer.observe(this, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ["mode", "show-proof"],
    });
    this.render();
  }
  disconnectedCallback() {
    this.observer?.disconnect();
    this.root?.unmount();
    this.root = undefined;
  }
  attributeChangedCallback(
    name: string,
    old: string | null,
    next: string | null,
  ) {
    if (old === next) return;
    this.render();
    if (name === "open") {
      this.dispatchEvent(
        new CustomEvent(next === null ? "proof:close" : "proof:open", {
          bubbles: true,
          composed: true,
          detail: { configuration: this.configuration },
        }),
      );
      if (next === null)
        queueMicrotask(() => {
          if (!this.hasAttribute("open") && this.previousFocus?.isConnected)
            this.previousFocus.focus();
        });
    }
  }
  private child() {
    return this.querySelector<Child>(
      "proof-authoring,proof-form,proof-form-configuration",
    );
  }
  private slotChanged = () => this.render();
  private modeOptions() {
    const child = this.child();
    if (child?.localName !== "proof-authoring") return [];
    const options = integrationOptionsSchema.parse(child.options || {});
    return options.allowedModes.filter(
      (mode) => options.permissions.edit || mode === "summary",
    );
  }
  private change(
    patch: Partial<IntegrationModalOptions> & {
      mode?: string;
      showProof?: boolean;
    },
  ) {
    const child = this.child();
    if (patch.mode !== undefined) {
      if (!this.modeOptions().includes(patch.mode as AuthoringMode)) {
        this.render();
        return;
      }
      child?.setAttribute("mode", patch.mode);
    }
    if (patch.showProof !== undefined) {
      if (
        child?.localName !== "proof-form" ||
        !integrationOptionsSchema.parse(child.options || {}).permissions.proof
      ) {
        this.render();
        return;
      }
      child.toggleAttribute("show-proof", patch.showProof);
    }
    const { mode: _, showProof: __, ...presentation } = patch;
    this.configuration = { ...this.configuration, ...presentation };
    this.dispatchEvent(
      new CustomEvent("proof:configuration-change", {
        bubbles: true,
        composed: true,
        detail: {
          configuration: this.configuration,
          mode: child?.getAttribute("mode"),
          showProof: child?.hasAttribute("show-proof") || false,
        },
      }),
    );
    this.render();
  }
  private render() {
    if (!this.root || !this.isConnected) return;
    const settings = this.configuration,
      child = this.child(),
      modes = this.modeOptions();
    const configuration =
      settings.configurable || settings.designMode ? (
        <ModalConfiguration
          settings={settings}
          modes={modes}
          mode={child?.getAttribute("mode") || "designer"}
          form={child?.localName === "proof-form"}
          showProof={child?.hasAttribute("show-proof") || false}
          canProof={
            child?.localName === "proof-form" &&
            integrationOptionsSchema.parse(child.options || {}).permissions
              .proof
          }
          onChange={(patch) => this.change(patch)}
          child={child || undefined}
          designMode={settings.designMode}
        />
      ) : undefined;
    this.root.render(
      <FormPresentation
        modal
        open={this.hasAttribute("open")}
        title={settings.title}
        triggerLabel={settings.triggerLabel}
        width={settings.width}
        height={settings.height}
        maxWidth={settings.maxWidth}
        maxHeight={settings.maxHeight}
        onOpen={this.showModal}
        onClose={this.close}
        closeLabel="Close integration"
        configuration={configuration}
      >
        <slot
          ref={(slot) => slot?.addEventListener("slotchange", this.slotChanged)}
        />
      </FormPresentation>,
    );
  }
}
const modalCss = `
:host{display:block;font-family:Inter,system-ui,sans-serif;color:var(--proof-popup-ink,var(--ink,#243b35))}
button,input,select{font:inherit}button{cursor:pointer;padding:9px 14px;border-radius:8px;border:1px solid #cad6cf;background:var(--proof-popup-button-surface,#edf4ef);color:var(--proof-popup-button-ink,#243b35)}
.embedded-dialog{min-width:min(320px,calc(100vw - 32px));min-height:120px;background:var(--proof-popup-surface,var(--surface,#fff));color:var(--proof-popup-ink,var(--ink,#243b35))}
.embedded-dialog-header{position:sticky;top:0;background:inherit;z-index:2;flex-wrap:wrap}.embedded-dialog-header h2{flex:1}
.popout-settings{border:1px solid #cad6cf;border-radius:10px;padding:16px;margin-bottom:20px}.popout-settings h3{margin-top:0}.popout-settings p{font-size:14px;line-height:1.5}
.popout-grid{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:12px}.popout-grid label{display:grid;gap:6px;min-width:0}.popout-grid input,.popout-grid select{box-sizing:border-box;width:100%;min-width:0;padding:8px;border:1px solid #cad6cf;border-radius:6px;background:inherit;color:inherit}
.embedded-dialog[open]{display:flex;flex-direction:column;overflow:hidden}.embedded-dialog-header{flex-shrink:0}.embedded-dialog-body{flex:1;min-height:0;overflow:auto;display:flex;flex-direction:column}.embedded-dialog-body>div:not([hidden]){flex-shrink:0}slot{display:block;min-width:0;flex:1;min-height:0}::slotted(*){display:block;height:100%;min-height:0}@media(max-width:600px){.popout-grid{grid-template-columns:1fr}.embedded-dialog-body{padding:12px}}
`;
export function registerProofIntegrationModal() {
  if (
    typeof customElements !== "undefined" &&
    !customElements.get("proof-integration-modal")
  )
    customElements.define(
      "proof-integration-modal",
      ProofIntegrationModalElement,
    );
}
